import React from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import Dialog from '../../toolkit/dialog';
import userService from '../../services/user-service';
import Pagination from '../widgets/pagination/Pagination';
import Logger from '../../toolkit/logger/Logger';
import AskPasswordDialog from '../common/AskPasswordDialog';
import TableHeader, { HeaderProps } from '../sensor/TableHeader';
import DataTable, { DataTableState } from '../widgets/data-table/DataTable';
import { num } from '../../utils/easy';
import auth, { getCurrentUser, resetToken } from '../../utils/auth';
import * as translate from '../../utils/translate';
import alert from '../../utils/alert';
import authService from '../../services/auth';

interface UserData {
  _id: string;
  access: string;
  email: string;
  name: string;
  parent: any;
  phoneNumber: string;
}

export interface State extends DataTableState {
  users: UserData[];
  search: string;
}

class UsersTable extends DataTable<RouteComponentProps, State> {

  private headOptions: HeaderProps = {
    items: [
      { text: '#', style: { width: '60px', textAlign: 'center' } },
      { text: 'نام', key: 'name' },
      { text: 'نوع کاربر', key: 'access' },
      { text: 'کاربر مافوق' },
      { text: 'فعالیت ها' }
    ]
  }

  constructor(props: RouteComponentProps) {
    super(props);
    this.state.users = [];
  }

  render() {
    const { search, pagination } = this.state;
    let index = ((pagination.current - 1) * pagination.limit) + 1;
    return (
      <React.Fragment>
        <div className="col-md-6 col-12 mx-auto">
          <div className="input-search-component ltr" style={{ marginBottom: '1rem' }}>
            <input
              style={{ textAlign: 'center' }}
              type="text"
              className="input-app input-md placeholder-rtl"
              value={this.state.search}
              placeholder="میان کاربران جستجو کنید..."
              onChange={this.onSearchTextChange} />
          </div>
        </div>
        <table className="table table-striped table-bordered text-center">
          <tbody style={{ width: '100%', display: 'table', background: '#fff' }}>
            <TableHeader items={this.headOptions.items} onSortChange={this.onSortTableChange} />
            {this.state.users.map(user => (
              <tr key={index}>
                <td className="text-center">{index++}</td>
                <td>{user.name}</td>
                <td>{translate.access(user.access)}</td>
                <td>{this.getParent(user)}</td>
                <td>
                  <div className="d-flex align-items-center justify-content-center">
                    <Link to={'/dashboard/edit-user/' + user._id}>
                      <i className="fal fa-edit fa-lg" style={{ color: '#f39c12' }}></i>
                    </Link>
                    {this.renderAdminActions(user)}
                    <Link to="#" onClick={() => { window.alert('این بخش به زودی تکمیل میشود.') }}>
                      <i className="fal fa-trash-alt fa-lg mr-2" style={{ color: '#e74c3c' }}></i>
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pagination.max === 1 ? ('') :
          (
            <Pagination
              searchKey="q"
              searchValue={search}
              current={num(pagination.current, 1)}
              max={num(pagination.max, 1)}
              onChange={this.onPaginationChange} />
          )
        }

      </React.Fragment>
    );
  }

  fetchData = async () => {
    Logger.info('UsersTable::fetchData called');
    const { search, pagination, sort } = this.state;
    try {
      const response = await userService.pureFetch({
        q: search,
        count: pagination.limit,
        page: pagination.current,
        sort: sort.key,
        sortOrder: sort.type
      });
      const headers = response.headers;
      if (response.status !== 200) return Logger.error(response);
      this.setState({
        users: response.data,
        pagination: {
          max: num(headers['pagination-count'], null),
          current: num(headers['pagination-page']),
          limit: num(headers['pagination-limit']),
        }
      }, () => Logger.info('UsersTable::fetchData State Updated'));
    }
    catch (e) {
      Logger.error(e);
    }
  }

  private getParent(user: UserData) {
    if (user.parent) {
      return user.parent.name;
    }
    return 'ندارد';
  }

  private renderAdminActions = (user: UserData) => {
    const auth = getCurrentUser();
    if (!auth || auth.access !== 'admin') {
      return '';
    }
    return (
      <React.Fragment>
        <button className="table-action-btn" onClick={this.handleLoginAsClicked.bind(this, user)}>
          <i className="fal fa-key fa-lg mr-2" style={{ color: '#9b59b6' }}></i>
        </button>
        <button className="table-action-btn" onClick={this.handlePasswordChange.bind(this, user)}>
          <i className="fal fa-lock fa-lg mr-2" style={{ color: '#2980b9' }}></i>
        </button>

      </React.Fragment>
    );
  }

  private handleLoginAsClicked = (user: UserData) => {
    const dialog = new Dialog({
      rtl: true,
      size: 'small',
      title: `آیا میخواهید به عنوان کاربر ${user.name} لاگین کنید؟`,
      message: AskPasswordDialog
    });
    const dialogProps = {
      action: this.requestLoginAs.bind(this, user._id),
      validatePassword: this.validatePassword
    }
    dialog.show(dialogProps);
  }

  private handlePasswordChange = (user: UserData) => {
    const dialog = new Dialog({
      rtl: true,
      size: 'small',
      title: `تغییر رمز عبور کاربر ${user.name}`,
      message: AskPasswordDialog
    });
    const dialogProps = {
      action: this.requestChangePassword.bind(this, user._id),
      validatePassword: this.validatePassword
    }
    dialog.show(dialogProps);
  }

  private validatePassword = (password: string) => {
    if (password.length === 0) {
      throw new Error('رمز عبور نمیتواند خالی باشد.');
    }
    return true;
  }

  private requestLoginAs = async (userId: string, password: string) => {
    const response = await authService.loginAlternate(userId, password);
    if (response.token != null) {
      auth.removeJwt();
      auth.setJwt(response.token);
      resetToken();
      window.location.href = '/dashboard';
    }
  }

  private requestChangePassword = async (userId: string, password: string) => {
    const response = await userService.changePassword(userId, password);
    if (response.password) {
      alert({
        type: 'success',
        message: `رمز عبور یکبار مصرف فعال شد - کد ورود ${response.password}`
      }).show();
    }
  }

}

export default withRouter(UsersTable);
